import { jsx as _jsx } from "react/jsx-runtime";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { withQueryParams } from "@faire/web--source/common/query-params/withQueryParams";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { useRefFrom } from "@faire/web--source/ui/hooks/useRefFrom";
import { observer } from "mobx-react";
import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSettingEnableMakerSignUpHooksMigration } from "@faire/retailer/settings/useSettingEnableMakerSignUpHooksMigration";
import { BrandsLandingPage } from "@faire/retailer/views/brands/landing-page/BrandsLandingPage";
import { LandingPage } from "@faire/retailer/views/brands/landing-page/WithOffer/LandingPage";
import { MakerSignUpAdditionalQuestions } from "./MakerApplicationAdditionalQuestions/MakerSignUpAdditionalQuestions";
import { MakerSignUpForm } from "./MakerSignUp/MakerSignUpForm";
import { MakerSignUpNotAccepted, SubmitPageStage } from "./MakerSignUp/MakerSignUpNotAccepted";
import { MakerApplicationStages, MakerSignUpState } from "./MakerSignUpState";
import { useMakerSignUp } from "./useMakerSignUp";
import { useMakerSignUpForm } from "./useMakerSignUpForm";
var LandingStatePage = function(param) {
    var skipToFormParam = param.skipToFormParam, viewState = param.viewState;
    var bonusOffersReferralNamesList = getGlobalProperty("referralBonusOfferReferralNames");
    var bonusOffersExist = isNotUndefined(bonusOffersReferralNamesList) && bonusOffersReferralNamesList.length > 0;
    if (bonusOffersExist && isNotUndefined(skipToFormParam)) {
        return /*#__PURE__*/ _jsx(LandingPage, {});
    }
    return /*#__PURE__*/ _jsx(BrandsLandingPage, {
        makerApplicationState: viewState
    });
};
var BrandApplicationBase = function(param) {
    var queryParams = param.queryParams;
    var enableMakerSignUpHooksMigration = useSettingEnableMakerSignUpHooksMigration();
    var strictLocalize = useStrictLocalization().strictLocalize;
    var history = useHistory();
    var viewState = useRefFrom(function() {
        return new MakerSignUpState(strictLocalize, history);
    }).current;
    var _useMakerSignUp = useMakerSignUp(), appStage = _useMakerSignUp.applicationStage, setApplicationStage = _useMakerSignUp.setApplicationStage;
    var _useMakerSignUpForm = useMakerSignUpForm(), initFormData = _useMakerSignUpForm.initializeFormData, setEmail = _useMakerSignUpForm.setEmailAddress, setBrandLeadToken = _useMakerSignUpForm.setBrandLeadToken, setTemporaryPosLinkKey = _useMakerSignUpForm.setTemporaryPosLinkKey;
    var initializeFormData = useCallback(function() {
        if (enableMakerSignUpHooksMigration) {
            initFormData();
        } else {
            viewState.initializeFormData();
        }
    }, [
        enableMakerSignUpHooksMigration,
        initFormData,
        viewState
    ]);
    var setEmailAddress = useCallback(function(emailAddress) {
        if (enableMakerSignUpHooksMigration) {
            setEmail(emailAddress);
        } else {
            viewState.setEmailAddress(emailAddress);
        }
    }, [
        enableMakerSignUpHooksMigration,
        setEmail,
        viewState
    ]);
    var applicationStage = useMemo(function() {
        if (enableMakerSignUpHooksMigration) {
            return appStage;
        }
        return viewState.applicationStage;
    }, [
        enableMakerSignUpHooksMigration,
        appStage,
        viewState.applicationStage
    ]);
    var skipToFormParam = queryParams.get("skip_to_form");
    useEffect(function() {
        var initializeBrandLeadToken = enableMakerSignUpHooksMigration ? setBrandLeadToken : function(brandLeadToken) {
            viewState.setBrandLeadToken(brandLeadToken);
        };
        var initializeTemporaryPosLinkKey = enableMakerSignUpHooksMigration ? setTemporaryPosLinkKey : function(temporaryPosLinkKey) {
            viewState.setTemporaryPosLinkKey(temporaryPosLinkKey);
        };
        var initializeApplicationStage = enableMakerSignUpHooksMigration ? setApplicationStage : function(applicationStage) {
            viewState.setApplicationStage(applicationStage);
        };
        initializeFormData();
        viewState.initializeCountryDropdownOption();
        if (queryParams.email_address) {
            setEmailAddress(queryParams.email_address);
        }
        var brandLeadToken = queryParams.get("blt");
        var temporaryPosLinkKey = queryParams.get("temporaryPosLinkKey");
        initializeBrandLeadToken(brandLeadToken ? "bl_".concat(brandLeadToken) : null);
        initializeTemporaryPosLinkKey(temporaryPosLinkKey !== null && temporaryPosLinkKey !== void 0 ? temporaryPosLinkKey : undefined);
        viewState.initialize();
        if (skipToFormParam) {
            initializeApplicationStage(MakerApplicationStages.FORM);
        }
    }, [
        queryParams,
        viewState,
        skipToFormParam,
        setEmailAddress,
        setBrandLeadToken,
        setTemporaryPosLinkKey,
        setApplicationStage,
        enableMakerSignUpHooksMigration,
        initializeFormData
    ]);
    switch(applicationStage){
        case MakerApplicationStages.LANDING:
            return /*#__PURE__*/ _jsx(LandingStatePage, {
                skipToFormParam: skipToFormParam,
                viewState: viewState
            });
        case MakerApplicationStages.FORM:
            return /*#__PURE__*/ _jsx(MakerSignUpForm, {
                viewState: viewState
            });
        case MakerApplicationStages.ADDITIONAL_QUESTIONS:
            var _viewState_brandLeadToken;
            return /*#__PURE__*/ _jsx(MakerSignUpAdditionalQuestions, {
                brandLeadToken: (_viewState_brandLeadToken = viewState.brandLeadToken) !== null && _viewState_brandLeadToken !== void 0 ? _viewState_brandLeadToken : ""
            });
        case MakerApplicationStages.WAITLIST:
            return /*#__PURE__*/ _jsx(MakerSignUpNotAccepted, {
                onClickTellUsMore: viewState.onClickTellUsMoreFunction,
                currentPage: SubmitPageStage.WAITLISTED
            });
        case MakerApplicationStages.UNSUPPORTED_AREA:
            return /*#__PURE__*/ _jsx(MakerSignUpNotAccepted, {
                currentPage: SubmitPageStage.UNSUPPORTED_AREA
            });
        default:
            return null;
    }
};
export var BrandApplication = withQueryParams(observer(BrandApplicationBase));
